<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import { IconWarning } from '@/components/design/icons';
import {
  createOverwrite,
  getVaultFileByRevision,
  updateOverwrite,
  type FileReviewInDb,
  type ReplacementSuggestion,
  type ReviewExtractionInDb,
} from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPageHeader from '@/components/design/composite/GaiaPageHeader.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaSplit from '@/components/design/base/GaiaSplit.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaExtractionDetail from '@/components/design/review/GaiaExtractionDetail.vue';
import GaiaReviewExportModal, {
  type ReviewExportRequest,
} from '@/components/design/review/GaiaReviewExportModal.vue';
import GaiaReviewFieldList from '@/components/design/review/GaiaReviewFieldList.vue';
import GaiaSuggestionDiff from '@/components/design/review/GaiaSuggestionDiff.vue';
import GaiaSummaryCard from '@/components/design/review/GaiaSummaryCard.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import { anyTaskStatusMap } from '@/components/design/statusMaps';
import { gaiaContentWidth, gaiaShellPadding } from '@/components/design/themes/tokens';
import type { GaiaExtractionEditValues } from '@/components/design/domain/extraction/GaiaExtractionFieldRow.vue';
import { useReviewLive } from '@/composables/review/useReviewLive';
import { useReviewPolling } from '@/composables/useReviewPolling';
import { useSummaryPolling } from '@/composables/useSummaryPolling';
import {
  buildExtractedField,
  groupFieldsByRisk,
  type ReviewExtractedField,
} from '@/services/review/extractedField';
import {
  DATA_EXPORT_COLUMNS,
  buildDataExportRows,
  exportFieldsCsv,
  exportFieldsXlsx,
} from '@/services/review/dataExport';
import { downloadCommentedDocx } from '@/services/review/commentedDocx';
import { exportSummaryMarkdown, exportSummaryPdf } from '@/services/review/summaryPdf';
import { useAuthStore } from '@/stores/auth';
import { useFileReviewStore } from '@/stores/fileReviewStore';
import { useReviewStore } from '@/stores/reviewStore';
import { buildOverwritePayload } from '@/utils/overwrite';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * One file's review: its AI summary, its extracted fields grouped by risk, and
 * the document beside them.
 *
 * The fields list, the two-card field body and the export dialog are design-
 * system components now (`GaiaReviewFieldList`, `GaiaExtractionDetail`,
 * `GaiaReviewExportModal`), so what is left here is the route contract, the
 * data plumbing, and the calls those components emit back.
 */

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const authStore = useAuthStore();
const reviewStore = useReviewStore();
const fileReviewStore = useFileReviewStore();

const reviewUid = computed(() => route.params.reviewId as string);
const fileReviewUid = computed(() => (route.query.fileReviewUid as string) || null);
const fileUid = computed(() => (route.query.fileUid as string) || null);

const pdfPreviewRef = ref<{
  goToPage: (page: number) => void;
  setHighlightText: (text: string[]) => void;
} | null>(null);
const pdfKey = ref(0);
const expandedUid = ref<string | undefined>(undefined);
const showExportModal = ref(false);
const isExporting = ref(false);
const signNavLoading = ref(false);
const downloadingFile = ref(false);

// ── Data ────────────────────────────────────────────────────────────────────

const fileReviews = computed<FileReviewInDb[]>(() =>
  reviewUid.value ? fileReviewStore.fileReviewsFor(reviewUid.value) : []
);

const currentFileReview = computed<FileReviewInDb | null>(() => {
  if (fileReviews.value.length === 0) return null;
  // `fileReviewUid` is what every current caller passes; `fileUid` (the static
  // copy's uid) is kept for links bookmarked before that changed.
  if (fileReviewUid.value) {
    return fileReviews.value.find(item => item.uid === fileReviewUid.value) ?? null;
  }
  if (fileUid.value) {
    return (
      fileReviews.value.find(item => item.static_vault_file_copy.uid === fileUid.value) ?? null
    );
  }
  return null;
});

const extractions = computed<ReviewExtractionInDb[]>(() => {
  const fileReview = currentFileReview.value;
  if (!reviewUid.value || !fileReview) return [];
  return fileReviewStore.extractionsFor(reviewUid.value, fileReview.uid);
});

const extractedFields = computed<ReviewExtractedField[]>(() =>
  extractions.value
    .map(extraction => buildExtractedField(extraction))
    .sort((a, b) => a.field.localeCompare(b.field))
);

const groups = computed(() => groupFieldsByRisk(extractedFields.value));

const canEdit = computed(() => authStore.userPermissions.can_update_reviews);
const isAdmin = computed(() => authStore.userPermissions.is_org_admin);

const hasSummary = computed(() => Boolean(currentFileReview.value?.markdown_summary));
const markdownSummary = computed(() => currentFileReview.value?.markdown_summary ?? '');

const extractionsProcessingComplete = computed(
  () =>
    extractions.value.length > 0 &&
    extractions.value.every(
      extraction => anyTaskStatusMap[extraction.processing_state].status === 'done'
    )
);

const fileName = computed(
  () =>
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    currentFileReview.value?.vault_file_revision?.file_name ||
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    currentFileReview.value?.static_vault_file_copy.file_name ||
    'Review'
);

// ── Document pane ───────────────────────────────────────────────────────────

const pdfFileUid = computed(
  () => currentFileReview.value?.static_vault_file_copy.uid ?? fileUid.value
);

const pdfRevisionPreviewStatus = computed(
  () =>
    currentFileReview.value?.vault_file_revision?.preview_status ??
    currentFileReview.value?.static_vault_file_copy.preview_status ??
    null
);

/** A static copy survived but the live revision is gone: no preview, no download. */
const isFileRevisionDeleted = computed(
  () =>
    currentFileReview.value !== null &&
    currentFileReview.value.vault_file_revision === null &&
    Boolean(currentFileReview.value.static_vault_file_copy)
);

/**
 * pdf.js sizes its canvases to the pane, so a resize has to remount the preview.
 * Debounced: re-keying on every frame of a drag would reload the document.
 */
const rekeyPdf = useDebounceFn(() => {
  pdfKey.value += 1;
}, 250);

function showInPdf(page: number, highlights: string[]): void {
  pdfPreviewRef.value?.goToPage(page);
  // Always called, even with an empty list — that is what clears the last field's
  // highlights when a different one is opened.
  pdfPreviewRef.value?.setHighlightText(highlights);
}

/** Opening a field jumps the document to where its answer came from. */
function handleSelect(field: ReviewExtractedField): void {
  expandedUid.value = field.uid;

  const page =
    field.has_page_overwrite && field.overwritten_page ? field.overwritten_page : field.ai_page;

  const overwritten = field.overwritten_highlighted_text;
  const highlights =
    field.has_highlighted_text_overwrite && Array.isArray(overwritten) && overwritten.length > 0
      ? overwritten
      : (field.ai_highlighted_text ?? []);

  showInPdf(page ?? 1, highlights);
}

function suggestionsFor(field: ReviewExtractedField): ReplacementSuggestion[] {
  const extraction = extractions.value.find(item => item.uid === field.uid);
  return extraction?.suggestion?.suggestions ?? [];
}

/** Platform admins only — the Langfuse trace behind one extraction. */
function traceUrlFor(field: ReviewExtractedField): string | undefined {
  if (!authStore.userPermissions.is_platform_admin) return undefined;
  return `https://cloud.langfuse.com/project/cm7by8x4m0027ad078d282b5c/traces?filter=metadata%3BstringObject%3Bextraction_uid%3Bcontains%3B${field.uid}&dateRange=All+time`;
}

function downloadFile(): void {
  if (!reviewStore.pdfSource) return;
  try {
    reviewStore.error = null;
    downloadingFile.value = true;
    const link = document.createElement('a');
    link.href = reviewStore.pdfSource;
    link.download = reviewStore.fileName;
    link.click();
  } catch (error) {
    logger.error('Error downloading file:', error);
    reviewStore.error = 'Failed to download file. Please try again.';
  } finally {
    downloadingFile.value = false;
  }
}

// ── Signing ─────────────────────────────────────────────────────────────────

const canSign = computed(
  () =>
    authStore.userPermissions.can_use_signing &&
    Boolean(currentFileReview.value?.vault_file_revision)
);

// Resolve the live revision back to its parent vault file, then hand off to the
// signature flow with the file pre-selected.
async function goToSigning(): Promise<void> {
  const revisionUid = currentFileReview.value?.vault_file_revision?.uid;
  if (!revisionUid) return;

  signNavLoading.value = true;
  try {
    const { data } = await getVaultFileByRevision({ path: { revision_uid: revisionUid } });
    if (!data?.uid) {
      message.error('Could not resolve the file to sign.');
      return;
    }
    await router.push({
      name: 'signing-request-new',
      query: { vault_file_uids: data.uid, from: 'review-detail' },
    });
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to open the signing flow.'));
  } finally {
    signNavLoading.value = false;
  }
}

// ── Edits ───────────────────────────────────────────────────────────────────

async function submitOverwrite(
  field: ReviewExtractedField,
  values: Map<string, GaiaExtractionEditValues>
): Promise<void> {
  const fileReview = currentFileReview.value;
  if (!fileReview) return;
  if (!canEdit.value) {
    message.error('You do not have permission to edit reviews.');
    return;
  }
  if (values.size === 0) {
    message.warning('No changes to submit');
    return;
  }

  const existing = field.overwrite_uid
    ? {
        value: field.overwritten_value,
        reason: field.overwritten_reason,
        page: field.overwritten_page,
        highlighted_text: field.overwritten_highlighted_text,
        evaluation: field.overwritten_evaluation,
        evaluation_reason: field.overwritten_evaluation_reason,
      }
    : null;

  try {
    const body = buildOverwritePayload(existing, values);

    await (field.overwrite_uid
      ? updateOverwrite({
          path: {
            review_uid: reviewUid.value,
            file_review_uid: fileReview.uid,
            extraction_uid: field.uid,
            extraction_overwrite_uid: field.overwrite_uid,
          },
          body,
        })
      : createOverwrite({
          path: {
            review_uid: reviewUid.value,
            file_review_uid: fileReview.uid,
            extraction_uid: field.uid,
          },
          body,
        }));

    message.success('Changes saved');
    await updateReviewDataOnly();
  } catch (error) {
    logger.error('Error submitting changes:', error);
    message.error('Failed to submit changes. Please try again.');
  }
}

async function handleApproval(field: ReviewExtractedField, approved: boolean): Promise<void> {
  const fileReview = currentFileReview.value;
  if (!fileReview) return;

  try {
    const updated = await reviewStore.updateExtractionByUid(
      reviewUid.value,
      fileReview.uid,
      field.uid,
      approved
    );
    if (!updated) {
      message.error('Failed to update approval status');
      return;
    }
    fileReviewStore.updateExtraction(reviewUid.value, fileReview.uid, field.uid, updated);
    message.success(`Extraction ${approved ? 'confirmed' : 'unconfirmed'} successfully`);
  } catch (error) {
    logger.error('Error updating approval status:', error);
    message.error('Failed to update approval status');
  }
}

// ── Export ──────────────────────────────────────────────────────────────────

const COLUMN_OPTIONS = DATA_EXPORT_COLUMNS.map(column => ({ label: column, value: column }));

const isWordDocument = computed(() => {
  const mimeType = (
    currentFileReview.value?.vault_file_revision?.mime_type ??
    currentFileReview.value?.static_vault_file_copy.mime_type
  )?.toLowerCase();
  if (!mimeType) return false;
  return mimeType.includes('word') || mimeType.includes('docx');
});

const hasSuggestions = computed(() =>
  extractions.value.some(extraction => (extraction.suggestion?.suggestions.length ?? 0) > 0)
);

const availableEvaluationAnswers = computed(() => {
  const answers = new Set<string>();
  for (const field of extractedFields.value) {
    const answer = field.ai_evaluation?.answer;
    if (Array.isArray(answer)) for (const value of answer) answers.add(value);
    else if (answer) answers.add(answer);
  }
  return [...answers].sort();
});

async function handleExport(request: ReviewExportRequest): Promise<void> {
  const fileReview = currentFileReview.value;
  if (!fileReview) {
    message.error('No data available for export');
    return;
  }

  isExporting.value = true;
  try {
    if (request.type === 'summary') {
      const summary = fileReview.markdown_summary;
      if (!summary) {
        message.error('No summary available for export');
        return;
      }
      if (request.format === 'pdf') exportSummaryPdf(summary, 'Contract Review Summary');
      else exportSummaryMarkdown(summary, 'Contract Review Summary');
      message.success('Summary exported successfully');
    } else if (request.type === 'commented-word') {
      await downloadCommentedDocx(
        reviewUid.value,
        fileReview.uid,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
        fileReview.vault_file_revision?.file_name ||
          fileReview.static_vault_file_copy.file_name ||
          'document',
        {
          include_agents: request.includeAgents,
          allowed_evaluation_answers:
            request.allowedAnswers.length > 0 ? request.allowedAnswers : undefined,
        }
      );
      message.success('Commented Word document exported successfully');
    } else {
      if (extractedFields.value.length === 0) {
        message.error('No data available for export');
        return;
      }
      const rows = buildDataExportRows(extractedFields.value, request.columns);
      if (request.format === 'csv') exportFieldsCsv(rows, 'Contract Review Data');
      else exportFieldsXlsx(rows, 'Contract Review Data');
      message.success('Data exported successfully');
    }
  } catch (error) {
    logger.error('Export error:', error);
    message.error(`Export failed: ${extractErrorDetail(error, 'Unknown error')}`);
  } finally {
    isExporting.value = false;
    showExportModal.value = false;
  }
}

// ── Loading and live updates ────────────────────────────────────────────────

async function loadFileReviews(): Promise<void> {
  await fileReviewStore.searchFileReviews(reviewUid.value, { text: '' });
  const loaded = fileReviewStore.fileReviewsFor(reviewUid.value);

  if (loaded.length === 0) {
    reviewStore.error = 'No file review found for this review';
    return;
  }

  const target = fileReviewUid.value
    ? loaded.find(item => item.uid === fileReviewUid.value)
    : fileUid.value
      ? loaded.find(item => item.static_vault_file_copy.uid === fileUid.value)
      : undefined;

  if (target) await fileReviewStore.loadExtractions(reviewUid.value, target.uid);
}

async function fetchData(): Promise<void> {
  if (!fileReviewUid.value && !fileUid.value) {
    reviewStore.error =
      'No file review or file was named. Please open a specific document to review.';
    return;
  }

  const review = await reviewStore.getReviewByUid(reviewUid.value);
  if (!review) return;
  reviewStore.current = review;

  await loadFileReviews();

  const target = currentFileReview.value;
  if (!target) return;

  reviewStore.pdfFileUid = target.static_vault_file_copy.uid;
  reviewStore.pdfRevisionUid = target.vault_file_revision?.uid ?? target.static_vault_file_copy.uid;

  // A deleted revision has nothing to fetch — the static copy is metadata only.
  if (!isFileRevisionDeleted.value) await reviewStore.fetchPdf();
}

/** Refetch without touching the PDF, so a websocket burst can't reload it. */
async function updateReviewDataOnly(): Promise<void> {
  if (!reviewUid.value || (!fileReviewUid.value && !fileUid.value)) return;
  try {
    reviewStore.error = null;
    await loadFileReviews();
  } catch (error) {
    // Deliberately not surfaced: this runs on a timer, and a failed background
    // refresh should not replace the data already on screen with an error.
    logger.error('Error updating review data:', error);
  }
}

const { stop: stopSummaryPolling } = useSummaryPolling(
  extractionsProcessingComplete,
  hasSummary,
  updateReviewDataOnly
);

// The socket and the poll fallback share one coalescing gate, so a burst of
// finished extractions costs one refetch rather than one each.
const { scheduleRefetch } = useReviewLive(reviewUid.value, updateReviewDataOnly);
const { stopPolling: stopEvaluationPolling } = useReviewPolling(reviewUid.value, () => {
  scheduleRefetch();
});

onMounted(async () => {
  await fetchData();
});

onBeforeUnmount(() => {
  stopSummaryPolling();
  stopEvaluationPolling();
  reviewStore.reset();
});

/**
 * The split's divider has to run edge to edge, so the layout column keeps no
 * side padding of its own; the panes carry it instead. The column's inset is
 * therefore the page edge minus the pane gutter, which puts pane content — and
 * the title above it — on the same 56px edge as every other page.
 */
const paneGutter = '16px';
const contentPadding = `0 calc(${gaiaShellPadding.pageX} - ${paneGutter})`;
const headerStyle = { flex: 'none', padding: `16px ${paneGutter} 12px` } as const;
const bodyStyle = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
} as const;
const splitStyle = { flex: '1 1 auto', minHeight: 0 } as const;
</script>

<template>
  <layout
    fill
    position="absolute"
    :content-max-width="gaiaContentWidth.wide"
    :content-padding="contentPadding"
  >
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.review }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'reviews' }">{{ navLabels.reviews }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ fileName }}</n-breadcrumb-item>
    </template>

    <div :style="bodyStyle">
      <gaia-page-header :title="fileName" :style="headerStyle">
        <template #meta>
          <gaia-status-indicator
            :status="extractionsProcessingComplete ? 'success' : 'running'"
            :glyph="extractionsProcessingComplete ? 'check' : 'dot'"
          />
          <gaia-text size="meta" tone="subtle">
            {{ extractionsProcessingComplete ? 'Review complete' : 'Review running' }}
          </gaia-text>
        </template>
        <template #actions>
          <gaia-button v-if="canSign" size="small" :loading="signNavLoading" @click="goToSigning">
            Sign
          </gaia-button>
          <gaia-button
            v-if="reviewStore.pdfSource"
            size="small"
            :loading="downloadingFile"
            @click="downloadFile"
          >
            Download
          </gaia-button>
          <gaia-button size="small" type="primary" @click="showExportModal = true">
            Export
          </gaia-button>
        </template>
      </gaia-page-header>

      <gaia-split :default-size="0.5" :style="splitStyle" @update:size="rekeyPdf">
        <template #1>
          <n-flex vertical :size="16" :wrap="false" class="pane">
            <gaia-banner v-if="reviewStore.error" type="error">{{ reviewStore.error }}</gaia-banner>

            <gaia-summary-card v-if="hasSummary">
              <markdown-renderer :content="markdownSummary" />
            </gaia-summary-card>
            <gaia-summary-card v-else-if="extractionsProcessingComplete">
              <gaia-text size="meta" tone="subtle"
                >No summary available for this document.</gaia-text
              >
            </gaia-summary-card>
            <gaia-summary-card v-else>
              <gaia-spinner size="small">
                <gaia-text size="meta" tone="subtle">Generating summary…</gaia-text>
              </gaia-spinner>
            </gaia-summary-card>

            <gaia-empty-state v-if="extractedFields.length === 0" title="No extracted fields yet">
              Fields appear here as the review finishes extracting them.
            </gaia-empty-state>

            <gaia-review-field-list
              v-else
              :groups="groups"
              :expanded-uid="expandedUid"
              @select="handleSelect"
            >
              <template #field="{ field }">
                <gaia-extraction-detail
                  :field="field"
                  :can-edit="canEdit"
                  :is-admin="isAdmin"
                  :trace-url="traceUrlFor(field)"
                  @submit="values => submitOverwrite(field, values)"
                  @approve="next => handleApproval(field, next)"
                  @page-click="(page, highlights) => showInPdf(page, highlights)"
                >
                  <template #suggestion>
                    <gaia-suggestion-diff
                      v-if="suggestionsFor(field).length > 0"
                      :suggestions="suggestionsFor(field)"
                      @select="(page, text) => showInPdf(page, [text])"
                    />
                  </template>
                </gaia-extraction-detail>
              </template>
            </gaia-review-field-list>
          </n-flex>
        </template>

        <template #2>
          <div class="pane">
            <gaia-vault-file-pdf-preview
              v-if="reviewStore.pdfSource && pdfFileUid"
              ref="pdfPreviewRef"
              :key="pdfKey"
              mode="paginated"
              :vault-file-uid="pdfFileUid"
              :revision-uid="reviewStore.pdfRevisionUid ?? undefined"
              :revision-preview-status="pdfRevisionPreviewStatus"
              :download-url="reviewStore.pdfSource"
            />
            <gaia-empty-state
              v-else-if="isFileRevisionDeleted"
              title="File revision deleted"
              status="warning"
            >
              <template #icon><n-icon :component="IconWarning" /></template>
              The revision attached to this review has been deleted. A static copy was preserved
              when the review was created, but live preview and download are no longer available.
              <template #meta>
                <gaia-text size="meta" tone="subtle">
                  {{ currentFileReview?.static_vault_file_copy.file_name }}
                </gaia-text>
              </template>
            </gaia-empty-state>
            <gaia-spinner v-else-if="reviewStore.loading" size="small">
              <gaia-text size="meta" tone="subtle">Loading document…</gaia-text>
            </gaia-spinner>
            <gaia-empty-state v-else title="No document available" status="info">
              No document source is available for preview.
              <template #actions>
                <gaia-button
                  v-if="reviewStore.fileName"
                  size="small"
                  type="primary"
                  :loading="downloadingFile"
                  @click="downloadFile"
                >
                  Download
                </gaia-button>
              </template>
            </gaia-empty-state>
          </div>
        </template>
      </gaia-split>
    </div>
  </layout>

  <gaia-review-export-modal
    v-model:show="showExportModal"
    :has-summary="hasSummary"
    :is-word-document="isWordDocument"
    :has-suggestions="hasSuggestions"
    :available-evaluation-answers="availableEvaluationAnswers"
    :column-options="COLUMN_OPTIONS"
    :default-columns="[...DATA_EXPORT_COLUMNS]"
    :exporting="isExporting"
    @export="handleExport"
  />
</template>

<style scoped>
/* Each half of the split scrolls on its own, so the page itself never does. */
.pane {
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding: 0 v-bind(paneGutter) v-bind(paneGutter);
}
</style>
